// src/components/Widgets/WorkshopsWidget.jsx
import React, { useMemo, useState, useId, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkshops } from "../../contexts/WorkshopContext";
import { workshopsApi } from "../../lib/mudoNetwork";
import { useActiveStudioId } from "../../hooks/useActiveStudioId";
import { useMaterials } from "../../contexts/MaterialContext";
import { useStorage } from "../../contexts/StorageContext";
import { navigateTo, ROUTES } from "../../lib/nav";
import WidgetHeader from "../Common/WidgetHeader";
import { useTheme } from "../../core/theme/ThemeProvider";
import { useWorkshopCreation, toLocalStartISO, formatMaterialsPerPerson } from "../../hooks/useWorkshopCreation";
import { supabase } from "../../supabaseClient";

// ---------- helpers ----------
const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const getStock = (m) =>
  toNum(m?.stock ?? m?.stockQty ?? m?.startQuantity ?? m?.quantity ?? 0);
const getUnit = (m) =>
  m?.conversionUnit || m?.trackingUnit || m?.unit || "unit";
const getUnitCost = (m) => {
  const cost = toNum(m?.purchaseCost);
  const conv = Math.max(1, toNum(m?.conversionAmount) || 1);
  return conv > 0 ? cost / conv : 0;
};

const newRowId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const emptyRow = { id: newRowId(), name: "", qty: "", unit: "g" };

// ---------- file paths (folder-based) ----------
const FS = {
  shopping: "shopping/shoppingList.json",
  draftPO_latest: "purchase-orders/draft-latest.json",
  draftPO_dir: "purchase-orders/drafts",
};

export default function WorkshopsWidget({
  onOpen,              // optional: open WorkshopBookingModal
  isDark = false,
  showBack = false,    // dashboard:false | standalone page:true
  backTo = "/dashboard",
}) {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const { workshops, bookings, createWorkshop, upsertWorkshop } = useWorkshops();
  const { studioId } = useActiveStudioId();
  const { materials = [], scrapedMaterials = [] } = useMaterials();
  const inv = (scrapedMaterials?.length ? scrapedMaterials : materials) || [];
  const datalistId = useId();

  const { hasFolder, readJSON, writeJSON, ensureDir } = useStorage();

  // ---------- Quick Create form state ----------
  const [qc, setQc] = useState({
    title: "",
    date: "",
    time: "",
    capacity: 10,
    price: 80,
    discountPercent: 10,
    materialsPerPerson: [{ ...emptyRow }],
  });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // ---------- Theming helpers ----------
  const inputClass =
    "w-full border rounded px-2 py-2 bg-slate-900/40 border-white/10 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[var(--mudo-primary)] focus:ring-1 focus:ring-[var(--mudo-primary)]";
  const selectClass = inputClass;
  const smallLabel = "text-[11px] text-slate-300";
  const sectionCard = "rounded border p-3 bg-slate-800/60 border-white/10";
  const primaryBtn =
    "px-3 py-2 rounded font-semibold transition bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] hover:brightness-110";
  const secondaryBtn =
    mode === "dark"
      ? "px-3 py-2 rounded border border-white/20 bg-white/5 hover:bg-white/10 text-white"
      : "px-3 py-2 rounded border border-black/15 bg-black/5 hover:bg-black/10 text-black";

  // ---------- Stats, next & today ----------
  const { stats, todayList, nextWorkshop } = useMemo(() => {
    const now = Date.now();

    const upcomingAll = [...(workshops || [])]
      .map((w) => ({
        ...w,
        _start: new Date(
          w.startISO || w.date || w.startAt || w.startsAt || 0
        ).getTime(),
      }))
      .filter((w) => Number.isFinite(w._start) && w._start >= now)
      .sort((a, b) => a._start - b._start);

    const byIdTaken = new Map();
    (bookings || []).forEach((b) => {
      const add = Number(b.seats) || 0;
      byIdTaken.set(b.workshopId, (byIdTaken.get(b.workshopId) || 0) + add);
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const todayOnly = upcomingAll.filter(
      (w) => w._start >= today.getTime() && w._start < tomorrow.getTime()
    );

    return {
      stats: {
        countUpcoming: upcomingAll.length,
        todayCount: todayOnly.length,
        seatsTakenFor: (id) => byIdTaken.get(id) || 0,
        next: upcomingAll[0] || null,
      },
      todayList: todayOnly,
      nextWorkshop: upcomingAll[0] || null,
    };
  }, [workshops, bookings]);

  // ---------- “Low / To Buy” computed from next workshop ----------
  const currencyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }),
    []
  );

  const lowToBuy = useMemo(() => {
    if (!nextWorkshop) return [];
    const byName = new Map(
      inv.map((m) => [String(m.name || "").toLowerCase(), m])
    );
    const byId = new Map(inv.map((m) => [String(m.id), m]));
    const reqs = Array.isArray(nextWorkshop.materialsPerPerson)
      ? nextWorkshop.materialsPerPerson
      : [];
    const cap = toNum(nextWorkshop.capacity || 0);

    return reqs
      .map((r) => {
        const key =
          (r.materialId && byId.get(String(r.materialId))) ||
          byName.get(String(r.name || "").toLowerCase());
        if (!key) return null;

        const unit = r.unit || getUnit(key);
        const unitCost = getUnitCost(key);
        const stock = getStock(key);
        const perPerson = toNum(r.qty || r.qtyPerPerson);
        const totalNeed = perPerson * cap;
        const deficit = Math.max(0, Math.ceil(totalNeed - stock));
        if (deficit <= 0) return null;

        const estCost = (unitCost || 0) * (deficit || 0);

        return {
          id: key.id,
          name: r.name || key.name || "Unnamed",
          sku: key.sku || "",
          category: key.category || "",
          image: typeof key.image === "string" ? key.image : null,
          unit,
          unitCost,
          stock,
          totalNeed,
          deficit,
          estCost,
        };
      })
      .filter(Boolean);
  }, [nextWorkshop, inv]);

  // ---------- Folder-based preview for recent shopping items ----------
  const [lowStockPreview, setLowStockPreview] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!hasFolder || lowToBuy.length > 0) {
        if (alive) setLowStockPreview([]);
        return;
      }
      try {
        const list = (await readJSON(FS.shopping, [])) || [];
        const arr = Array.isArray(list) ? list : [];
        if (alive) setLowStockPreview(arr.slice(-3).reverse());
      } catch {
        if (alive) setLowStockPreview([]);
      }
    })();
    return () => { alive = false; };
  }, [hasFolder, readJSON, lowToBuy.length]);

  // ---------- Materials editor ----------
  const updateMatRow = (idx, field, val) => {
    setQc((s) => ({
      ...s,
      materialsPerPerson: s.materialsPerPerson.map((r, i) =>
        i === idx ? { ...r, [field]: val } : r
      ),
    }));
  };
  const addMatRow = () =>
    setQc((s) => ({
      ...s,
      materialsPerPerson: [...s.materialsPerPerson, { ...emptyRow, id: newRowId() }],
    }));
  const removeMatRow = (idx) =>
    setQc((s) => ({
      ...s,
      materialsPerPerson: s.materialsPerPerson.filter((_, i) => i !== idx),
    }));



  // ---------- Publish all public workshops to Supabase ----------
  const publishAllPublicToSupabase = async () => {
    if (syncing) return;
    try {
      setSyncing(true);
      const { data: sess } = await supabase.auth.getSession();
      const session = sess?.session || null;
      const user = session?.user || null;
      
      if (!user) {
        alert("Please sign in to publish workshops.");
        return;
      }

      if (!studioId) {
        alert("No studio ID found. Please set up your studio profile first.");
        return;
      }

      console.log('[WorkshopsWidget] Publishing with studioId:', studioId);
      console.log('[WorkshopsWidget] User ID:', user.id);

      // Check if studio exists in Supabase
      const { data: existingStudio, error: studioCheckErr } = await supabase
        .from('studios')
        .select('id, name, owner_user_id, is_public')
        .eq('id', studioId)
        .maybeSingle();

      console.log('[WorkshopsWidget] Studio check result:', { existingStudio, studioCheckErr });

      if (studioCheckErr) {
        console.error('[WorkshopsWidget] Studio check error:', studioCheckErr);
        alert(`Error checking studio: ${studioCheckErr.message}`);
        return;
      }

      if (!existingStudio) {
        console.log('[WorkshopsWidget] Studio not found in Supabase, creating...');
        // Studio doesn't exist in Supabase - create it
        const { data: newStudio, error: createErr } = await supabase
          .from('studios')
          .insert({
            id: studioId,
            owner_user_id: user.id,
            name: user.user_metadata?.name || user.email?.split('@')[0] || 'My Studio',
            is_public: true,
            email: user.email,
          })
          .select()
          .single();

        console.log('[WorkshopsWidget] Studio creation result:', { newStudio, createErr });

        if (createErr) {
          console.error('[WorkshopsWidget] Studio creation error:', createErr);
          alert(`Failed to create studio in network: ${createErr.message}\n\nPlease go to Settings → Studio Profile to set up your studio.`);
          return;
        }
        
        console.log('[WorkshopsWidget] Created studio:', newStudio);
      } else if (existingStudio.owner_user_id !== user.id) {
        alert(`Studio ${studioId} exists but is owned by another user. You can only publish workshops for studios you own.`);
        return;
      } else if (!existingStudio.is_public) {
        // Studio exists but is private - update it to public
        console.log('[WorkshopsWidget] Studio is private, updating to public...');
        const { error: updateErr } = await supabase
          .from('studios')
          .update({ is_public: true })
          .eq('id', studioId);
        
        if (updateErr) {
          console.error('[WorkshopsWidget] Failed to make studio public:', updateErr);
          alert(`Your studio is private and we couldn't make it public automatically.\n\nError: ${updateErr.message}`);
          return;
        }
        
        console.log('[WorkshopsWidget] Studio updated to public');
      }

      let count = 0;
      let skipped = 0;
      let errors = 0;
      
      for (const w of workshops || []) {
        const isPublic = w.isPublic === true || w.is_published === true;
        if (!isPublic) {
          skipped++;
          continue;
        }

        const { data: remote, error } = await workshopsApi.upsertWorkshop(
          {
            title: w.title,
            description: w.description,
            startISO: w.startISO || w.date || w.startAt || w.startsAt,
            durationMins: Number(w.durationMins || w.duration || 120),
            capacity: Number(w.capacity || 0),
            price: Number(w.price || 0),
            level: w.level || w.skill_level,
            teaserDataUrl: w.teaserDataUrl || w.hero_image_url,
            isPublic: true,
            studioId,
            networkId: w.networkId || w.remoteId || w.id_remote,
          },
          { fallbackStudioId: studioId }
        );

        if (!error && remote?.id) {
          count++;
          await upsertWorkshop({ id: w.id, networkId: remote.id });
          console.log(`[WorkshopsWidget] Published: ${w.title} → ${remote.id}`);
        } else {
          errors++;
          console.warn(`[WorkshopsWidget] Failed to publish: ${w.title}`, error);
        }
      }

      if (count > 0) {
        alert(`✅ Published ${count} workshop${count > 1 ? 's' : ''} to the network!\n\n${skipped > 0 ? `Skipped ${skipped} private workshop${skipped > 1 ? 's' : ''}\n` : ''}${errors > 0 ? `${errors} error${errors > 1 ? 's' : ''} (check console)` : ''}\n\nVisitors can now see your workshops at ceramics.mudo.app/discover/workshops`);
      } else {
        alert(`No public workshops to publish.\n\n${skipped > 0 ? `Found ${skipped} private workshop${skipped > 1 ? 's' : ''} (not published)\n` : ''}${errors > 0 ? `${errors} error${errors > 1 ? 's' : ''} occurred` : ''}\n\nTip: Check "Make Public" when creating workshops to make them visible to visitors.`);
      }
    } catch (e) {
      console.error("[WorkshopsWidget] Publish all failed", e);
      alert("❌ Publish failed. See console for details.");
    } finally {
      setSyncing(false);
    }
  };

  // ---------- Quick Create handler (using shared hook) ----------
  const { handleCreateWorkshop } = useWorkshopCreation();

  const submitQuickCreate = async (e) => {
    e.preventDefault();
    if (!qc.title.trim() || !qc.date || !qc.time) {
      alert("Please fill Title, Date and Time.");
      return;
    }
    try {
      setBusy(true);

      // Use shared creation hook
      await handleCreateWorkshop({
        title: qc.title.trim(),
        description: "",
        date: qc.date,
        time: qc.time,
        durationMins: 120,
        capacity: Math.max(1, Number(qc.capacity) || 1),
        price: Math.max(0, Number(qc.price) || 0),
        discountPercent: Math.max(0, Math.min(100, Number(qc.discountPercent) || 0)),
        type: "Hand-building",
        level: "All Levels",
        materialsPerPerson: qc.materialsPerPerson,
        isPublic: true,
        teaserDataUrl: null,
      }, {
        onSuccess: () => {
          // Reset form
          setQc((s) => ({
            ...s,
            title: "",
            date: "",
            time: "",
            materialsPerPerson: [{ ...emptyRow, id: newRowId() }],
          }));
        },
        onError: (err) => {
          alert(err?.message || "Failed to create workshop");
        },
      });
    } catch (err) {
      console.error("[submitQuickCreate]", err);
    } finally {
      setBusy(false);
    }
  };

  // ---------- Create draft PO from lowToBuy (folder-first) ----------
  const createDraftFromNeeds = async () => {
    if (!lowToBuy.length) return;

    const draft = {
      id: `draft-${Date.now()}`,
      status: "Draft",
      createdAt: Date.now(),
      items: lowToBuy.map((l) => ({
        materialId: l.id,
        name: l.name,
        sku: l.sku,
        supplier: "",
        website: "",
        image: l.image,
        unit: l.unit,
        unitCost: Number(l.unitCost || 0),
        qty: Number(l.deficit || 0),
        category: l.category || "",
      })),
      meta: {
        source: "workshop",
        workshopTitle: nextWorkshop?.title || "",
        workshopDate: nextWorkshop?.startISO || null,
      },
    };

    try {
      if (hasFolder) {
        await ensureDir(FS.draftPO_dir);
        // write latest marker (simple place for Purchase Orders page to read)
        await writeJSON(FS.draftPO_latest, draft);
        // also persist a dated file
        await writeJSON(`${FS.draftPO_dir}/${draft.id}.json`, draft);
      } else {
        // Fallback-only when no folder linked (kept for backward compatibility)
        try { localStorage.setItem("mudoDraftPO", JSON.stringify(draft)); } catch {}
      }
    } catch (e) {
      console.error("[workshops] failed to create draft PO", e);
      alert("Couldn't create the draft PO in the selected folder.");
      return;
    }

    navigateTo(navigate, ROUTES.PURCHASE_ORDERS, { source: 'WorkshopsWidget:createPO' });
  };

  const remainingFor = (w) =>
    Math.max(0, (Number(w.capacity) || 0) - stats.seatsTakenFor(w.id));

  // Count public workshops for status indicator
  const publicCount = useMemo(() => {
    return (workshops || []).filter(w => w.isPublic === true || w.is_published === true).length;
  }, [workshops]);

  const publishedCount = useMemo(() => {
    return (workshops || []).filter(w => w.networkId || w.remoteId || w.id_remote).length;
  }, [workshops]);

  return (
    <div className="space-y-3">
      {showBack ? (
        <WidgetHeader title=" Workshops" to={backTo} />
      ) : (
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg"> Workshops</h3>
          <div className="flex gap-2">
            <button
              onClick={() =>
                typeof onOpen === "function" ? onOpen() : navigateTo(navigate, ROUTES.WORKSHOPS, { source: 'WorkshopsWidget:openButton' })
              }
              className="px-3 py-1 text-sm rounded text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              Open Workshops
            </button>
          </div>
        </div>
      )}

      {/* Key stats */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className={sectionCard}>
          <div className={smallLabel}>Upcoming</div>
          <div className="text-xl font-semibold">{stats.countUpcoming}</div>
        </div>
        <div className={sectionCard}>
          <div className={smallLabel}>Today</div>
          <div className="text-xl font-semibold">{stats.todayCount}</div>
        </div>
        <div className={sectionCard}>
          <div className={smallLabel}>Total</div>
          <div className="text-xl font-semibold">{workshops?.length || 0}</div>
        </div>
      </div>

      {/* Publish status alert - show when any workshops exist */}
      {workshops?.length > 0 && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-lg">ℹ️</span>
            <div className="flex-1">
              <div className="font-medium text-blue-200 mb-1">
                {workshops.length} workshop{workshops.length > 1 ? 's' : ''} in your studio
              </div>
              <p className="text-xs text-blue-300/90">
                Open each workshop and use the "Visitor Visibility" toggle to make it public. It will then appear in the discover page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Next up */}
      {stats.next ? (
        <div className={`${sectionCard} text-sm`}>
          <div className={`${smallLabel} mb-1`}>Next</div>
          <div className="font-medium">{stats.next.title}</div>
          <div className="text-xs opacity-70">
            {new Date(stats.next.startISO).toLocaleString()} • {stats.next.type} • {stats.next.level}
          </div>
          <div className="text-xs mt-1">
            Capacity: {stats.next.capacity} • Taken: {stats.seatsTakenFor(stats.next.id)} • Remaining:{" "}
            {remainingFor(stats.next)}
          </div>
        </div>
      ) : (
        <div className="text-xs opacity-70">No upcoming workshops yet.</div>
      )}

      {/* Low / To Buy */}
      <div className={sectionCard}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium text-sm"> Low / To Buy</div>
          {lowToBuy.length > 0 ? (
            <button
              onClick={createDraftFromNeeds}
              className="text-xs px-2 py-1 rounded bg-amber-500 text-black hover:bg-amber-400"
              title="Create a draft PO from these needs"
            >
              manage
            </button>
          ) : (
            <button
              onClick={() =>
                typeof onOpen === "function" ? onOpen() : navigateTo(navigate, ROUTES.WORKSHOPS, { source: 'WorkshopsWidget:manageButton' })
              }
              className="text-xs underline opacity-70 hover:opacity-100"
              title="Open Workshops"
            >
              manage
            </button>
          )}
        </div>

        {lowToBuy.length > 0 ? (
          <ul className="space-y-1 text-xs">
            {lowToBuy.map((l) => (
              <li key={l.id} className="flex justify-between gap-2">
                <span className="truncate">
                  {l.name}{" "}
                  <span className="opacity-60">
                    (Workshop {stats.next?.title || "workshop"})
                  </span>
                </span>
                <span className="shrink-0 tabular-nums">
                  {l.deficit} {l.unit}{" "}
                  {Number.isFinite(l.estCost) && l.estCost > 0
                    ? `• ${currencyFmt.format(l.estCost)}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : lowStockPreview.length > 0 ? (
          <ul className="space-y-1 text-xs">
            {lowStockPreview.map((it) => (
              <li key={it.id} className="flex justify-between gap-2">
                <span className="truncate">
                  {it.name}{" "}
                  <span className="opacity-60">
                    ({it.reason || "workshop"})
                  </span>
                </span>
                <span className="shrink-0">
                  {it.qty}
                  {it.unit ? ` ${it.unit}` : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-xs opacity-60">
            Nothing needed for the next workshop.
          </div>
        )}
      </div>

      {/* Quick create */}
      <form onSubmit={submitQuickCreate} className={`${sectionCard} space-y-2 text-sm`}>
        <div className="font-medium"> Quick Create</div>

        <label className="block">
          <div className={smallLabel}>Title</div>
          <input
            className={inputClass}
            placeholder="e.g., Hand-Building Basics"
            value={qc.title}
            onChange={(e) => setQc({ ...qc, title: e.target.value })}
            aria-label="Quick create title"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label>
            <div className={smallLabel}>Date</div>
            <input
              type="date"
              className={inputClass}
              value={qc.date}
              onChange={(e) => setQc({ ...qc, date: e.target.value })}
              aria-label="Quick create date"
            />
          </label>
          <label>
            <div className={smallLabel}>Time</div>
            <input
              type="time"
              className={inputClass}
              value={qc.time}
              onChange={(e) => setQc({ ...qc, time: e.target.value })}
              aria-label="Quick create time"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <label>
            <div className={smallLabel}>Capacity</div>
            <input
              type="number"
              min={1}
              className={inputClass}
              placeholder="10"
              value={qc.capacity}
              onChange={(e) =>
                setQc({ ...qc, capacity: Math.max(1, Number(e.target.value) || 1) })
              }
              aria-label="Quick create capacity"
            />
          </label>
          <label>
            <div className={smallLabel}>Price (AUD)</div>
            <input
              type="number"
              min={0}
              className={inputClass}
              placeholder="80"
              value={qc.price}
              onChange={(e) =>
                setQc({ ...qc, price: Math.max(0, Number(e.target.value) || 0) })
              }
              aria-label="Quick create price"
            />
          </label>
          <label>
            <div className={smallLabel}>App Discount %</div>
            <input
              type="number"
              min={0}
              max={100}
              className={inputClass}
              placeholder="10"
              value={qc.discountPercent}
              onChange={(e) =>
                setQc({
                  ...qc,
                  discountPercent: Math.max(
                    0,
                    Math.min(100, Number(e.target.value) || 0)
                  ),
                })
              }
              aria-label="Quick create discount"
            />
          </label>
        </div>

        <div className="border rounded p-2">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Materials Per Participant</div>
            <button type="button" onClick={addMatRow} className="text-xs underline" aria-label="Add material row">
              + Add
            </button>
          </div>

          <datalist id={datalistId}>
            {inv.map((m) => (
              <option key={String(m.id ?? m.sku ?? m.name)} value={m.name} />
            ))}
          </datalist>

          <div className="space-y-2">
            {qc.materialsPerPerson.map((row, idx) => (
              <div key={row.id} className="grid grid-cols-12 gap-2 items-start">
                <label className="col-span-6">
                  <div className={smallLabel}>Material</div>
                  <input
                    list={datalistId}
                    className={inputClass}
                    placeholder="Start typing to search inventory…"
                    value={row.name}
                    onChange={(e) => updateMatRow(idx, "name", e.target.value)}
                    aria-label={`Quick create material name row ${idx + 1}`}
                  />
                </label>

                <label className="col-span-3">
                  <div className={smallLabel}>Qty</div>
                  <input
                    type="number"
                    min={0}
                    className={inputClass}
                    placeholder="e.g., 1500"
                    value={row.qty}
                    onChange={(e) => updateMatRow(idx, "qty", e.target.value)}
                    aria-label={`Quick create material qty row ${idx + 1}`}
                  />
                </label>

                <label className="col-span-2">
                  <div className={smallLabel}>Unit</div>
                  <select
                    className={selectClass}
                    value={row.unit}
                    onChange={(e) => updateMatRow(idx, "unit", e.target.value)}
                    aria-label={`Quick create material unit row ${idx + 1}`}
                  >
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="ml">ml</option>
                    <option value="l">l</option>
                    <option value="pcs">pcs</option>
                  </select>
                </label>

                <div className="col-span-1 flex items-end">
                  <button
                    type="button"
                    className={
                      isDark
                        ? "w-full border rounded px-2 py-2 border-zinc-700"
                        : "w-full border rounded px-2 py-2 border-zinc-300"
                    }
                    onClick={() => removeMatRow(idx)}
                    title="Remove row"
                    aria-label={`Remove material row ${idx + 1}`}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>

          <p className={`${smallLabel} mt-2`}>
            Tip: These quantities are per person. We’ll multiply by capacity and
            convert into a draft PO from “Low / To Buy” if stock looks short.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            disabled={busy}
            className="px-3 py-1 rounded text-white transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {busy ? "Creating…" : "Create"}
          </button>
          <button
            type="button"
            className={
              isDark
                ? "px-3 py-1 rounded border border-zinc-700"
                : "px-3 py-1 rounded border border-zinc-300"
            }
            onClick={() =>
              setQc({
                title: "",
                date: "",
                time: "",
                capacity: 10,
                price: 80,
                discountPercent: 10,
                materialsPerPerson: [{ ...emptyRow, id: newRowId() }],
              })
            }
          >
            Reset
          </button>
        </div>

        <p className={smallLabel}>
          Inventory needs are auto-calculated and can be pushed to a draft PO.
        </p>
      </form>

      {/* Today list */}
      {todayList.length > 0 && (
        <div className={`${sectionCard} text-xs`}>
          <div className="font-medium text-sm mb-1">Today</div>
          <ul className="space-y-1">
            {todayList.map((w) => {
              const taken = stats.seatsTakenFor(w.id);
              return (
                <li key={w.id} className="flex justify-between gap-2">
                  <span className="truncate">{w.title}</span>
                  <span className="shrink-0">
                    {new Date(w.startISO).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    • {remainingFor(w)} left
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
